import React, { useEffect, useState } from 'react';
import { Form, Button, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { spotifySettingsApi } from '../api/api';

export default function AllowedPlaylistsPicker({ value, onChange }) {
  const [playlists, setPlaylists] = useState([]);
  const [filteredPlaylists, setFilteredPlaylists] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Parse comma-separated IDs into a set
  const selectedIds = new Set(
    value
      ? value.split(',').map((id) => id.trim()).filter((id) => id)
      : []
  );

  // Load playlists on mount
  useEffect(() => {
    fetchPlaylists();
  }, []);

  // Filter playlists when search changes
  useEffect(() => {
    const filtered = playlists.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredPlaylists(filtered);
  }, [search, playlists]);

  const fetchPlaylists = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await spotifySettingsApi.getPlaylists();
      setPlaylists(res.data.playlists || []);
    } catch (err) {
      setError('Konnte Playlists nicht laden. Stelle sicher, dass Spotify verbunden ist.');
      console.error('Error fetching playlists:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (playlistId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(playlistId)) {
      newSelected.delete(playlistId);
    } else {
      newSelected.add(playlistId);
    }
    const newValue = Array.from(newSelected).join(', ');
    onChange(newValue || null);
  };

  const handleClearAll = () => {
    onChange(null);
  };

  const handleSelectAll = () => {
    const allIds = filteredPlaylists.map((p) => p.id).join(', ');
    onChange(allIds || null);
  };

  return (
    <Form.Group className="mb-4">
      <Form.Label className="small">Erlaubte Playlists (kommagetrennte IDs)</Form.Label>

      {error && <Alert variant="warning" className="small mb-2">{error}</Alert>}

      {/* Suchfeld */}
      <InputGroup size="sm" className="mb-2">
        <Form.Control
          placeholder="Playlist suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-dark text-white border-secondary"
          disabled={loading}
        />
        {loading && <Spinner animation="border" size="sm" />}
        <Button variant="outline-secondary" onClick={fetchPlaylists} disabled={loading}>
          🔄
        </Button>
      </InputGroup>

      {/* Playlist Liste */}
      <div className="bg-dark rounded border border-secondary p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {loading && (
          <div className="text-center">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {!loading && filteredPlaylists.length === 0 && (
          <p className="text-muted small mb-0">
            {search ? 'Keine Playlists gefunden.' : 'Keine Playlists verfügbar.'}
          </p>
        )}

        {!loading &&
          filteredPlaylists.map((p) => (
            <Form.Check
              key={p.id}
              type="checkbox"
              id={`playlist-${p.id}`}
              label={p.name}
              checked={selectedIds.has(p.id)}
              onChange={() => handleToggle(p.id)}
              className="mb-2"
            />
          ))}
      </div>

      {/* Action Buttons */}
      {!loading && playlists.length > 0 && (
        <div className="d-flex gap-2 mt-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleSelectAll}
            disabled={selectedIds.size === playlists.length}
          >
            Alles auswählen
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleClearAll}
            disabled={selectedIds.size === 0}
          >
            Alle abwählen
          </Button>
        </div>
      )}

      {/* Selected IDs Display */}
      {selectedIds.size > 0 && (
        <Form.Text className="text-muted d-block mt-2">
          <strong>Ausgewählte Playlists ({selectedIds.size}):</strong>
          <br />
          <code>{Array.from(selectedIds).join(', ')}</code>
        </Form.Text>
      )}

      <Form.Text className="text-muted text-end d-block mt-1">
        {playlists.length} Playlists verfügbar
      </Form.Text>

      <Form.Text className="text-muted d-block mt-2">
        Nur Songs aus diesen Playlists können angefragt werden. Leer = alle erlaubt.
      </Form.Text>
    </Form.Group>
  );
}
